// Local wrapper around the vendored calendar (see ~/vendor/ra-calendar)
import type {
  DateSelectArg,
  DatesSetArg,
  DateUnselectArg,
  EventApi,
  EventClickArg,
  EventContentArg,
} from '@fullcalendar/core'
// FullCalendar 6 moved its internals out of the public entry point.
import { getSegAnchorAttrs, type Seg, type ViewContext } from '@fullcalendar/core/internal'
import AddIcon from '@mui/icons-material/Add'
import isEqual from 'lodash/isEqual'
import { useState } from 'react'
import { Button, useListContext } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import {
  getFilterValuesFromInterval as defaultGetFilterValuesFromInterval,
  Calendar as OriginalCalendar,
  CalendarProps as OriginalCalendarProps,
} from '~/vendor/ra-calendar'

export interface EventContentArgExt extends EventContentArg {
  titleOverride: any
}

const renderInnerContent = (innerProps: EventContentArgExt) => {
  const { titleOverride } = innerProps
  return (
    <div className='fc-event-main-frame'>
      {innerProps.timeText && <div className='fc-event-time'>{innerProps.timeText}</div>}
      <div className='fc-event-title-container'>
        <div className='fc-event-title fc-sticky'>
          {titleOverride || innerProps.event.title || <>&nbsp;</>}
        </div>
      </div>
    </div>
  )
}

const renderDayGridInnerContent = (innerProps: EventContentArgExt) => {
  const { titleOverride } = innerProps
  return (
    // workaround: Wrap in span element to be usable with tooltip
    <span
      className='fc-daygrid-dot-event'
      style={{
        overflow: 'auto',
        width: '100%',
        background: innerProps.isSelected ? 'rgba(0,0,0,0.1)' : '',
      }}
    >
      <div
        className='fc-daygrid-event-dot'
        style={{ borderColor: innerProps.borderColor || innerProps.backgroundColor }}
      />
      {innerProps.timeText && <div className='fc-event-time'>{innerProps.timeText}</div>}
      <div className='fc-event-title'>{titleOverride || innerProps.event.title || <>&nbsp;</>}</div>
    </span>
  )
}

const renderListInnerContent = (seg: Seg, context: ViewContext) => {
  const { titleOverride } = seg
  // FullCalendar's internal helpers are not part of its published types.
  let interactiveAttrs = getSegAnchorAttrs(seg, context) as any
  return (
    <a {...interactiveAttrs}>
      {/* TODO: document how whole row become clickable */}
      {titleOverride || seg.event.title}
    </a>
  )
}

export const renderEventInnerContent = (props: EventContentArgExt) => {
  const {
    event: { _context, _def, _instance },
  } = props
  // `_context`/`_def`/`_instance` and options.type are FullCalendar internals.
  const options = _context.options as any
  return options.type === 'list'
    ? renderListInnerContent(
        { ...props, eventRange: { def: _def, instance: _instance } } as any,
        _context as any
      )
    : options.type === 'dayGrid'
      ? renderDayGridInnerContent(props)
      : renderInnerContent(props)
}

export const Calendar = (props: CalendarProps) => {
  const navigate = useNavigate()

  const [selectedEventId, setSelectedEventId] = useState('')

  const [selectedDate, setSelectedDate] = useState<{
    selectInfo: DateSelectArg | null
    placeholderEvent: EventApi | null
  }>({
    selectInfo: null,
    placeholderEvent: null,
  })

  const {
    getFilterValueFromInterval = defaultGetFilterValuesFromInterval,
    datesSetExtend,
    eventContentExtend,
    eventContent,
    onEventSelect,
    onEventUnselect,
    ...rest
  } = props

  const { data, filterValues, setFilters, displayedFilters, resource } = useListContext()

  const selectEvent = (id: string) => {
    setSelectedEventId(id)
    onEventSelect && onEventSelect(id)
  }

  const unselectEvent = () => {
    onEventUnselect && onEventUnselect(selectedEventId)
    setSelectedEventId('')
  }

  const handleDatesSet = (dateInfo: DatesSetArg): void => {
    unselectEvent()
    const newFilterValues = getFilterValueFromInterval(dateInfo, filterValues)
    if (!isEqual(filterValues, newFilterValues)) {
      setFilters(newFilterValues, displayedFilters)
    }
    // Allow extending callback function
    if (datesSetExtend) datesSetExtend(dateInfo)
  }

  const handleDateSelect = (selectInfo: DateSelectArg): void => {
    const calendarApi = selectInfo.view.calendar
    if (selectedDate.placeholderEvent) {
      selectedDate.placeholderEvent.remove()
    }
    const { allDay, start, end, view } = selectInfo
    const day = 24 * 60 * 60 * 1000
    const override: { start?: Date; end?: Date; allDay?: boolean } = {}
    if (allDay && view.type === 'dayGridMonth' && end.getTime() - start.getTime() <= day) {
      override.start = new Date(start)
      override.end = new Date(start)
      override.start.setHours(6, 0)
      override.end.setHours(7, 0)
      override.allDay = false
    }
    setSelectedDate({
      selectInfo,
      placeholderEvent: calendarApi.addEvent({
        allDay: allDay,
        start: start,
        end: end,
        ...override,
        title: 'Add new event?',
        editable: false,
        color: 'rgba(200,200,200, 0.5)',
      }),
    })
    // calendarApi.unselect(); // clear date selection
    // navigate(`/${resource}/create`, { state: {
    //   record: {
    //       start: selectInfo.startStr,
    //       end: selectInfo.endStr,
    //       allDay: selectInfo.allDay,
    //   },
    // } })
  }

  const handleDateUnselect = (selectInfo: DateUnselectArg): void => {
    if (selectedDate.placeholderEvent) {
      selectedDate.placeholderEvent.remove()
    }
    setSelectedDate({ selectInfo: null, placeholderEvent: null })
  }

  const handleEventClick = (clickInfo: EventClickArg): void => {
    const { event } = clickInfo
    if (event.id) {
      if (selectedEventId === event.id) {
        navigate(`/${resource}/${event.id}`)
      } else {
        unselectEvent()
        selectEvent(event.id)
      }
    } else {
      navigate(`/${resource}/create`, {
        state: {
          record: {
            start: event.startStr,
            end: event.endStr,
            allDay: event.allDay,
          },
        },
      })
    }
  }

  const renderEventContent = (arg: EventContentArg) => {
    return arg.event.id ? (
      eventContentExtend ? (
        eventContentExtend(
          { ...arg, isSelected: arg.event.id === selectedEventId, unselect: unselectEvent },
          renderEventInnerContent
        )
      ) : (
        eventContent
      )
    ) : (
      // <>{renderEventInnerContent(arg)}</>
      <Button
        fullWidth
        style={{ height: 'inherit' }}
        size={arg.event.allDay || arg.view.type === 'dayGridMonth' ? 'small' : 'large'}
        // label='resources.appointments.new'
        onMouseUp={() => handleEventClick(arg as any)}
      >
        <AddIcon />
      </Button>
    )
  }

  return (
    <OriginalCalendar
      {...rest}
      datesSet={handleDatesSet}
      eventContent={renderEventContent}
      select={handleDateSelect}
      unselectCancel='.fc-event-main, .MuiTooltip-tooltip'
      unselect={handleDateUnselect}
      selectMirror={false}
      eventClick={handleEventClick}
    />
  )
}

export interface CalendarProps extends OriginalCalendarProps {
  datesSetExtend: (arg: DatesSetArg) => void
  eventContentExtend: (
    arg: EventContentExtendArg,
    renderInner: (arg: EventContentArgExt) => any
  ) => any
  onEventSelect?: (event_id: string) => void
  onEventUnselect?: (event_id: string) => void
}

export interface EventContentExtendArg extends EventContentArg {
  isSelected: boolean
  unselect: () => void
}

export default Calendar
